import { ReactNode, createContext, useEffect, useState } from 'react';

import { ICaseFileModel } from '../interfaces/ICaseFileModel';
import { UseQueryResult } from 'react-query';
import { UserSettingTypesEnum } from '../utilities/Constants';
import { useAuthContext } from './AuthContext';
import { useGetUserCaseFileModels } from '../pages/quad/useGetUserCaseFileModels';
import { useGetUserSetting } from '../components/user-settings/useGetUserSetting';

export interface CaseFileModelContextType {
    caseFileModelId: number;
    caseFileModelIdChange: (id: number) => void;
    userCaseFileModelsQuery?: UseQueryResult<ICaseFileModel[] | undefined, unknown>;
}

const CaseFileModelContext = createContext<CaseFileModelContextType | undefined>(undefined);

const CaseFileModelProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { isLoggedIn } = useAuthContext();
    const userCaseFileModelsQuery = useGetUserCaseFileModels(isLoggedIn);
    const preferredSameCaseZoneGroupSetting = useGetUserSetting(
        UserSettingTypesEnum.PreferredSameCaseZoneGroup,
        isLoggedIn
    );

    const isLoadingUserSettings = preferredSameCaseZoneGroupSetting.isLoading;

    const [caseFileModelId, setCaseFileModelId] = useState<number>(0);

    const caseFileModelIdChange = (id: number) => {
        setCaseFileModelId(id);
    };

    useEffect(() => {
        if (isLoggedIn && !isLoadingUserSettings) {
            if (
                caseFileModelId === 0 &&
                userCaseFileModelsQuery.data &&
                preferredSameCaseZoneGroupSetting.data
            ) {
                const preferredSameCaseZoneGroupIdId = parseInt(
                    preferredSameCaseZoneGroupSetting?.data?.settingsJson ?? '',
                    0
                );

                const option =
                    preferredSameCaseZoneGroupIdId > 0
                        ? userCaseFileModelsQuery?.data?.find(
                              (c) => c.id == preferredSameCaseZoneGroupIdId
                          )
                        : userCaseFileModelsQuery?.data?.[0];
                caseFileModelIdChange(option?.id ?? 0);
            }
        }
    }, [
        isLoadingUserSettings,
        isLoggedIn,
        userCaseFileModelsQuery.data,
        preferredSameCaseZoneGroupSetting,
    ]);

    return (
        <CaseFileModelContext.Provider
            value={{
                caseFileModelId,
                caseFileModelIdChange,
                userCaseFileModelsQuery,
            }}
        >
            {children}
        </CaseFileModelContext.Provider>
    );
};

export { CaseFileModelProvider, CaseFileModelContext };
